import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export type ChipTone = 'neutral' | 'accent' | 'live' | 'night'

export interface ChipProps extends BoxProps {
  tone?: ChipTone
  icon?: React.ReactNode
}

const toneStyles: Record<ChipTone, React.CSSProperties> = {
  neutral: { background: 'var(--ds-hover)', color: 'var(--ds-ink-muted)' },
  accent: {
    background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))',
    color: 'var(--ds-accent-deep-b, var(--ds-accent))',
  },
  live: { background: 'var(--ds-ink)', color: '#fff' },
  night: { background: 'rgba(244, 243, 246, 0.08)', color: 'var(--ds-night-ink, #fff)' },
}

const Root = styled(Box, {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: ChipTone }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 30,
  padding: '0 13px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '-0.005em',
  whiteSpace: 'nowrap',
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  ...toneStyles[tone],
}))

/**
 * Static informational pill — announcement tags, plan badges, metadata
 * labels. Non-interactive; for a selectable pill row use `ChipToggleGroup`.
 */
export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(function Chip(
  { tone = 'neutral', icon, children, ...rest },
  ref,
) {
  return (
    <Root ref={ref} tone={tone} {...rest}>
      {/* Root's flex `gap` only creates space *between* flex items — plain
          text (e.g. icon='❤') and `children` rendered as bare siblings with
          no element between them count as one contiguous anonymous text
          run, not two items, so the gap silently never applied. Spans give
          each a real element boundary so gap has something to land between. */}
      {icon != null && <span>{icon}</span>}
      <span>{children}</span>
    </Root>
  )
})
